import type { ReactNode } from 'react'
import { ChevronsLeft, ChevronsRight, LayoutDashboard, Settings, type LucideIcon } from 'lucide-react'
import { NavLink } from 'react-router-dom'
import { BrandMark } from './BrandMark.tsx'
import { iconForDepartment } from './DepartmentIcon.tsx'
import { DEPARTMENTS } from '../lib/departments.ts'

type NavItem = { to: string; label: string; icon: LucideIcon }

/**
 * Primary navigation, grouped by section: a quiet mono group label with its
 * items beneath. Items deliberately omit NavLink's `end` prop so a parent stays
 * highlighted while you're on a child route (a department during its filtered view).
 */
const GROUPS: { label: string; items: NavItem[] }[] = [
  { label: 'Overview', items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard }] },
  {
    label: 'Departments',
    items: DEPARTMENTS.map(({ slug, name }) => ({
      to: `/departments/${slug}`,
      label: name,
      // Its own glyph, shared with the catalog cards. Seven of the same
      // building told a reader nothing, and at the collapsed rail width the
      // icon is the only thing left to tell them apart.
      icon: iconForDepartment(slug),
    })),
  },
  { label: 'Admin', items: [{ to: '/settings', label: 'Settings', icon: Settings }] },
]

export function Sidebar({
  collapsed = false, onToggle, children,
}: {
  collapsed?: boolean
  onToggle?: () => void
  children?: ReactNode
}) {
  return (
    <aside className="sidebar">
      <div className="sidebar__brand">
        <BrandMark small />
      </div>

      <nav className="sidebar__nav" aria-label="Primary">
        {GROUPS.map((group) => {
          const labelId = `navgroup-${group.label.toLowerCase()}`
          return (
            <div key={group.label} className="navgroup" role="group" aria-labelledby={labelId}>
              <p className="navgroup__label" id={labelId}>
                {group.label}
              </p>
              {group.items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) => (isActive ? 'navlink is-active' : 'navlink')}
                  // A name too long for the current rail width is truncated on
                  // screen; this is how it can still be read without resizing.
                  title={label}
                >
                  <Icon size={16} strokeWidth={1.9} aria-hidden="true" />
                  <span>{label}</span>
                </NavLink>
              ))}
            </div>
          )
        })}
      </nav>

      {onToggle && (
        <button
          type="button"
          className="sidebar__fold"
          onClick={onToggle}
          aria-expanded={!collapsed}
          // The label names the outcome, and stays constant so the control
          // does not appear to change identity as it is used.
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          {collapsed
            ? <ChevronsRight size={15} strokeWidth={1.9} aria-hidden="true" />
            : <ChevronsLeft size={15} strokeWidth={1.9} aria-hidden="true" />}
          <span>Collapse</span>
        </button>
      )}

      {/* The resize handle, when the shell supplies one. */}
      {children}
    </aside>
  )
}
